import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { LabelingEntry } from './labelResultEntry';
import { Categories } from './categories';
import { PopupDialogComponent } from '../popup/popup.component';
import { AuthService } from '../services/auth.service';
import { environment } from '../../environments/environment';
import { DocHistory } from '../model/docHistory';
import { DbAccessService } from '../services/dbAccess.service';
import { MessageService } from '../services/message.service';

@Component({
  selector: 'app-labeling',
  templateUrl: './labeling.component.html',
  styleUrls: ['./labeling.component.css'],
})
export class LabelingComponent implements OnInit {
  appVersion = environment.app_version;
  currentDBEntry: LabelingEntry;
  page = 1;
  totalPages: number;
  isLoaded = false;
  selectedCat = '';
  selectedCats: Array<string>;
  cats = Categories.cats;
  pdfObject = null;
  docHardtoRead = false;
  docHardtoReads: Array<boolean>;
  hasComment = '';
  hasDbComment = '';
  dialogActive = false;
  docLoading = false;
  docHistory: Array<DocHistory> = [];
  docHistCount = 0;
  pdfRotation = 0;
  pdfZoom = 1;
  author: string;
  notes = '';

  constructor(
    private dialog: MatDialog,
    private dbAccessService: DbAccessService,
    private router: Router,
    private messageService: MessageService,
    public authService: AuthService,
  ) {}

  ngOnInit() {
    this.author = this.authService.getAuthor();
    if (this.author !== '') {
      this.loadNextDocument();
    } else {
      this.messageService.add('Kein angemeldeter Bearbeiter gefunden.', 'error');
    }
  }

  afterLoadComplete(pdfData: any): void {
    this.totalPages = pdfData.numPages;
    this.isLoaded = true;
    if (typeof this.currentDBEntry.category !== 'undefined' && this.currentDBEntry.category.length > 0) {
      if (!Array.isArray(this.selectedCats)) {
        this.selectedCats = this.currentDBEntry.category;
      }
      if (!Array.isArray(this.docHardtoReads)) {
        this.docHardtoReads = this.currentDBEntry.hardtoread || [];
      }
      this.selectedCat = this.selectedCats[0];
      this.docHardtoRead = this.docHardtoReads[0];
    } else {
      this.selectedCats = [];
      this.docHardtoReads = [];
    }
    this.messageService.clear();
  }

  onPdfError(error: any): void {
    console.log(`ERROR: ${error}`);
    this.openErrorDialog(error);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft') {
      this.prevPage();
    }
    if (event.key === 'ArrowRight') {
      this.nextPage();
    }
  }

  savePageCat(): void {
    this.selectedCats[this.page - 1] = this.selectedCat;
    this.docHardtoReads[this.page - 1] = this.docHardtoRead;
  }

  nextPage(): void {
    this.savePageCat();
    if (this.page < this.totalPages) {
      if (this.selectedCats[this.page] === '' || this.selectedCats[this.page] === undefined) {
        this.selectedCat = this.selectedCats[this.page - 1];
        this.docHardtoRead = this.docHardtoReads[this.page - 1];
      } else {
        this.selectedCat = this.selectedCats[this.page];
        this.docHardtoRead = this.docHardtoReads[this.page];
      }
      this.page++;
    } else {
      if (!this.dialogActive) {
        if (this.hasDbComment !== '' && this.notes === '') {
          this.openDialog(this.hasDbComment);
        } else if (this.notes !== '') {
          this.openDialog(this.notes);
        } else {
          this.openDialog();
        }
      }
    }
  }

  prevPage(): void {
    if (this.page > 1) {
      this.savePageCat();
      this.page--;
      this.selectedCat = this.selectedCats[this.page - 1];
      this.docHardtoRead = this.docHardtoReads[this.page - 1];
    }
  }

  saveDocument(state: string, comment?: string): Promise<void> {
    return new Promise((resolve) => {
      this.docLoading = true;
      const entry = { ...this.currentDBEntry };
      entry.state = state;
      entry.comment = comment;
      entry.author = this.authService.getAuthor();
      entry.category = this.selectedCats;
      entry.hardtoread = this.docHardtoReads;
      this.dbAccessService.deleteItem(this.currentDBEntry).then(() => {
        // console.log('Item deleted');
      });
      this.dbAccessService.putItem(entry).then(() => {
        this.hasDbComment = '';
        this.messageService.add('Das Dokument wurde gespeichert.', 'success');
        resolve();
      });
    });
  }

  loadNextDocument(docUri?: string, state?: string): void {
    this.docLoading = true;
    this.isLoaded = false;
    this.docHistCount = 0;
    // this.messageService.clear();

    if (docUri) {
      if (state && state === 'review') {
        this.selectDocumentFromUri(docUri, state);
      } else {
        this.selectDocumentFromUri(docUri);
      }
      this.docLoading = false;
      return;
    }

    if (this.authService.author === 'Reviewer') {
      this.selectInReviewDocument();
    } else {
      this.selectInProgressDocument();
    }
  }

  getDoneDocuments(): void {
    this.docHistory = [];
    this.dbAccessService
      .selectDoneDocuments()
      .then(() => {
        if (this.dbAccessService.docHistory.length !== 0) {
          const docArray = this.dbAccessService.docHistory;
          this.docHistCount = docArray.length;
          this.docHistory = docArray.sort((a, b) => (a.timestamp < b.timestamp ? 1 : b.timestamp < a.timestamp ? -1 : 0)).slice(0, 199);
        }
      })
      .catch((error) => {
        console.log('Error: ', error);
      });
  }

  getCategoriesFromEntry(): void {
    if (this.currentDBEntry.category.length > 0) {
      this.selectedCats = [];
      this.docHardtoReads = [];
      this.currentDBEntry.category.forEach((category) => {
        this.selectedCats.push(category);
      });
      this.currentDBEntry.hardtoread.forEach((hardtoread) => {
        this.docHardtoReads.push(hardtoread);
      });
    }
  }

  selectDocumentFromUri(docString: string, state?: string): void {
    this.dbAccessService
      .selectDocumentFromUri(docString, state)
      .then((docEntry: LabelingEntry) => {
        if (docEntry) {
          this.currentDBEntry = docEntry;
          this.getCategoriesFromEntry();
          this.hasComment = this.getFormatetComment(this.currentDBEntry);
          this.updatePdfView();
          this.docHistCount = this.docHistory.length;
        } else {
          this.messageService.add('Keine Dokumente in der History gefunden.', 'info');
        }
      })
      .catch((error) => {
        console.log('Error: ', error);
      });
  }

  selectInProgressDocument(): void {
    this.dbAccessService
      .selectInProgressDocument()
      .then((inProgressDocEntry: LabelingEntry) => {
        if (inProgressDocEntry) {
          this.currentDBEntry = inProgressDocEntry;
          this.updatePdfView();
        } else {
          this.dbAccessService
            .stageNewDocument()
            .then((newDocEntry: LabelingEntry) => {
              if (newDocEntry) {
                this.loadNextDocument();
              } else {
                this.messageService.add('Keine weiteren Dokumente gefunden.', 'info');
                this.docLoading = false;
                this.isLoaded = true;
                this.pdfObject = null;
              }
            })
            .catch((error) => {
              console.log('Error: ', error);
            });
        }
        this.docLoading = false;
      })
      .catch((error) => {
        console.log('Error: ', error);
      });
  }

  selectInReviewDocument(): void {
    this.dbAccessService
      .selectInReviewDocument()
      .then((inReviewDocEntry: LabelingEntry) => {
        if (inReviewDocEntry) {
          this.currentDBEntry = inReviewDocEntry;
          this.getCategoriesFromEntry();
          this.hasComment = this.getFormatetComment(this.currentDBEntry);
          this.updatePdfView();
        } else {
          this.messageService.add('Keine Dokumente in Review gefunden.', 'info');
          this.pdfObject = null;
        }
        this.docLoading = false;
      })
      .catch((error) => {
        console.log('Error: ', error);
      });
  }

  private getFormatetComment(entry: LabelingEntry): string {
    if (typeof entry.comment === 'undefined') {
      return '<p>Kein Kommentar vorhanden.</p>';
    } else if (entry.comment !== '') {
      console.log('rev comment: ', entry.comment);
      return `<p><b>Author:</b> ${entry.author}</p><p><b>Kommentar:</b> ${entry.comment}</p>`;
    }
    return '<p>Leerer Kommentar.</p>';
  }

  updatePdfView(): void {
    this.selectedCat = '';
    this.page = 1;
    this.retrievePdfObject(this.currentDBEntry.doc_uri);
  }

  retrievePdfObject(url: string): void {
    const rewrittenUrl = url
      .replace('https://api.europace2.de/v1/dokumente', '/dokumente')
      .replace('http://ep-doc-doctor.services.ps.rz-hypoport.local/v1/dokumente', '/dokumente');
    this.dbAccessService.retrievePdfObject(rewrittenUrl).subscribe(
      (result: ArrayBuffer) => {
        if (result) {
          this.pdfObject = result; // console.log('pdfObject retrieved', result);
        } else {
          this.pdfObject = null;
          console.log('No further docs...');
        }
      },
      (error) => {
        console.log('Error: ', error);
        this.openErrorDialog(error.message);
      },
    );
  }

  openDialog(comment?: string): void {
    let configObj = { width: '500px', data: {} };
    if (comment) {
      configObj = { width: '500px', data: { comment: comment } };
    }
    const dialogRef = this.dialog.open(PopupDialogComponent, configObj);
    this.dialogActive = true;
    dialogRef.afterClosed().subscribe((result) => {
      if (result.state) {
        this.saveDocument(result.state, result.comment)
          .then(() => {
            this.docLoading = false;
            this.notes = '';
            this.loadNextDocument();
          })
          .catch((error) => {
            console.log('Error: ', error);
          });
        this.dialogActive = false;
      }
      this.dialogActive = false;
    });
  }

  openErrorDialog(error) {
    const dialogRef = this.dialog.open(PopupDialogComponent, {
      width: '500px',
      data: {
        error: error,
      },
    });
    this.dialogActive = true;
    dialogRef.afterClosed().subscribe((result) => {
      if (result.state === 'error') {
        this.saveDocument('error', error)
          .then(() => {
            this.docLoading = false;
            this.loadNextDocument();
          })
          .catch((err) => {
            console.log('Error: ', err);
          });
      } else if (result.state === 'review') {
        this.saveDocument('review', error)
          .then(() => {
            this.docLoading = false;
            this.loadNextDocument();
          })
          .catch((err) => {
            console.log('Error: ', err);
          });
      }
      this.dialogActive = false;
    });
  }

  logoutFromPage(): void {
    this.authService.logout();
    this.messageService.clear();
    this.router.navigate(['/login-page']);
  }

  rotateDocumentClockwise(): void {
    this.pdfRotation += 90;
  }

  rotateDocumentCclockwise(): void {
    this.pdfRotation -= 90;
  }

  rotateDocumentReset(): void {
    this.pdfRotation = 0;
  }

  zoomDocumentIn(): void {
    this.pdfZoom += 0.1;
  }

  zoomDocumentOut(): void {
    if (this.pdfZoom > 0.2) {
      this.pdfZoom -= 0.1;
    }
  }

  zoomDocumentReset(): void {
    this.pdfZoom = 1;
  }
}
